import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TablePagination } from '../../../components/common/TablePagination';
import { FormDatePicker } from '../../../components/Form/FormDatePicker';
import { useCasePermissions } from '../../../hooks/UseCasePermission';
import { usePloSixTableFilters } from '../../../hooks/plo/ploSixTableFilter';
import {
  deletePloForm,
  fetchPloApplications,
} from '../../../services/PloSixService';
import type { User } from '../../../types/user';
import { formatDateToMyanmar } from '../../../utils/GlobalUtils';

export function PLOFormSixTable() {
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const filters = usePloSixTableFilters();

  const [currentUser] = useState<User | null>(() => {
    if (typeof window === 'undefined') return null;
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  });

  useCasePermissions(currentUser);

  // Memoized helper to check if logged-in user is the creator/origin user
  const checkIsOriginUser = useCallback(
    (app: any): boolean => {
      if (!currentUser) return false;

      const originOfficeId =
        app.originOfficeId ||
        app.originOffice?.id ||
        app.document?.originOfficeId ||
        app.officeId;

      if (
        currentUser.officeId &&
        originOfficeId &&
        currentUser.officeId === originOfficeId
      ) {
        return true;
      }

      const createdById = app.createdById || app.createdBy?.id;
      if (currentUser.id && createdById && currentUser.id === createdById) {
        return true;
      }

      return false;
    },
    [currentUser]
  );

  // Query - fixed missing section filter dependency & fetch parameter
  const { data, isLoading, isError, error, isPlaceholderData, refetch, isFetching } =
    useQuery({
      queryKey: [
        'ploFormSix',
        filters.debouncedSearch,
        filters.debouncedCourt,
        filters.debouncedParty,
        filters.debouncedSection, // Added if debounced in hook, or use filters.sectionSearch
        filters.selectedStatus,
        filters.selectedDate,
        filters.currentPage,
      ],
      queryFn: () =>
        fetchPloApplications({
          searchTerm: filters.debouncedSearch,
          courtSearch: filters.debouncedCourt,
          partySearch: filters.debouncedParty,
          sectionSearch: filters.sectionSearch, // FIX: Passed to backend call
          selectedStatus: filters.selectedStatus,
          selectedDate: filters.selectedDate,
          currentPage: filters.currentPage,
          itemsPerPage,
        }),
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    });

  const rawList = Array.isArray(data) ? data : data?.data;
  const applications = Array.isArray(rawList) ? rawList : [];
  const totalItems = data?.totalItems || applications.length || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handleDelete = async (id?: string) => {
    if (!id) return;
    if (!window.confirm('ဤမှတ်တမ်းကို ဖျက်ရန် သေချာပါသလား။')) return;

    try {
      await deletePloForm(id);
      await refetch();
    } catch (err: any) {
      console.error('Delete failed:', err);
      alert(err?.message || 'ဖျက်ခြင်း မအောင်မြင်ပါ။');
    }
  };

  return (
    <div className="space-y-4 bg-white p-4 md:p-6 rounded-2xl border border-slate-200 shadow-sm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900 tracking-tight">
            PLOပုံစံ (၆) စာရင်းများ
          </h2>
        </div>

        <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors disabled:opacity-50"
            title="ဒေတာများ နောက်ဆုံး Data များ ရယူရန်"
          >
            <svg
              className={`h-3.5 w-3.5 ${isFetching ? 'animate-spin text-blue-600' : 'text-slate-500'}`}
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
              />
            </svg>
            နောက်ဆုံး Data များ ရယူရန်
          </button>

          <button
            type="button"
            onClick={() => navigate('/plo-form-six/create')}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-500 transition-colors shrink-0"
          >
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
            ဖောင်အသစ်ထည့်ရန်
          </button>
        </div>
      </div>

      {/* Filter Controls Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 items-end bg-slate-50/60 p-3 rounded-xl border border-slate-100">
        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">အထွေထွေ ရှာဖွေရန်</label>
          <input
            type="text"
            placeholder="စာရွက်စာတမ်းနံပါတ်/အမှုနံပါတ်..."
            value={filters.searchTerm}
            onChange={(e) => {
              filters.setSearchTerm(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">တရားလို/တရားခံ</label>
          <input
            type="text"
            placeholder="တရားလို/တရားခံ အမည်..."
            value={filters.partySearch}
            onChange={(e) => {
              filters.setPartySearch(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-500 mb-1">တရားစွဲဆိုသည့် ဥပဒေ၊ ပုဒ်မ</label>
          <input
            type="text"
            placeholder="ဥပဒေ/ပုဒ်မ ရှာရန်..."
            value={filters.sectionSearch || ''}
            onChange={(e) => {
              filters.setSectionSearch?.(e.target.value);
              filters.setCurrentPage(1);
            }}
            className="w-full rounded-md border border-slate-200 bg-white py-1.5 px-2.5 text-xs text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <div className="flex-1">
            <label className="block text-[11px] font-medium text-slate-500 mb-1">တွေ့ဆုံသည့် နေ့ရက်</label>
            <FormDatePicker
              value={filters.selectedDate}
              onChange={(e) => {
                filters.setSelectedDate(e.target.value);
                filters.setCurrentPage(1);
              }}
              name="selectedDate"
              className="w-full"
            />
          </div>

          {filters.hasActiveFilters && (
            <button
              type="button"
              onClick={filters.handleClearFilters}
              title="စစ်ထုတ်မှုများ ဖျက်ရန်"
              className="mt-5 shrink-0 inline-flex items-center justify-center text-xs font-medium text-rose-600 hover:bg-rose-50 border border-rose-200 rounded-md py-1.5 px-2 bg-white transition-colors"
            >
              🔄
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="w-full overflow-x-auto border border-black relative">
        {isPlaceholderData && (
          <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] flex items-center justify-center z-10">
            <span className="text-xs bg-slate-800 text-white px-2.5 py-1 rounded-full shadow-sm">
              နောက်ဆုံးပေါ်အချက်အလက် ရယူနေပါသည်...
            </span>
          </div>
        )}

        <table className="w-full text-center border-collapse text-xs min-w-[850px]">
          <thead>
            <tr className="bg-slate-50 border-b border-black font-bold">
              <th className="border-r border-black p-2 w-12">
                စဉ်
              </th>
              <th className="border-r border-black p-2 w-28">
                ရာဇဝတ်ကြီးမှု<br />အမှတ်
              </th>
              <th className="border-r border-black p-2 w-24">
                တရားလို
              </th>
              <th className="border-r border-black p-2 w-24">
                တရားခံ
              </th>
              <th className="border-r border-black p-2 w-32">
                တရားစွဲဆိုသည့်<br />ဥပဒေ၊ ပုဒ်မ
              </th>
              <th className="border-r border-black p-2 w-32">
                နစ်နာသူ /<br />မျက်မြင်သက်သေ
              </th>
              <th className="border-r border-black p-2 w-36">
                တွေ့ဆုံဆွေးနွေးသည့်<br />ဥပဒေအရာရှိအမည်
              </th>
              <th className="border-r border-black p-2 w-28">
                တွေ့ဆုံသည့်<br />နေ့ရက်
              </th>
              <th className="border-r border-black p-2 w-28">
                မှတ်ချက်
              </th>
              <th className="p-2 w-16 font-sans text-gray-400 font-normal">Action</th>
            </tr>
            <tr className="bg-slate-50 border-b border-black text-[10px] font-normal">
              {['(၁)', '(၂)', '(၃)', '(၄)', '(၅)', '(၆)', '(၇)', '(၈)', '(၉)'].map((number) => (
                <th key={number} className="border-r border-black px-2 py-1">{number}</th>
              ))}
              <th className="p-1" aria-hidden="true" />
            </tr>
          </thead>
          <tbody>
            {isLoading && !isPlaceholderData ? (
              <tr>
                <td colSpan={10} className="text-center py-12 text-xs text-slate-400">
                  အချက်အလက်များ ဆွဲယူနေပါသည်...
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={10} className="text-center py-12 text-xs text-red-500">
                  {error instanceof Error ? error.message : 'အချက်အလက်များ ဆွဲယူရာတွင် အမှားအယွင်းရှိခဲ့ပါသည်။'}
                </td>
              </tr>
            ) : applications.length > 0 ? (
              applications.map((app: any, index: number) => {
                const rowId = app.id || app._id;
                const formDetails = app.pLOFormSix || app.ploFormSix || app.formData || app;

                const caseNo = formDetails.caseNo || formDetails.caseNumber || '-';
                const plaintiffName = formDetails.plaintiff || formDetails.plaintiffName || '-';
                const defendantName = formDetails.defendant || formDetails.defendantName || '-';
                const lawSection = formDetails.lawSection || '-';
                const witnessName = formDetails.witnessName || '-';
                const officerName = formDetails.officerName || '-';
                const remarks = formDetails.remarks || '-';

                const formattedInterviewDate =
                  formatDateToMyanmar(formDetails.interviewDate) || '-';

                const isOriginUser = checkIsOriginUser(app);

                return (
                  <tr key={rowId || index} className="border-b border-gray-400 hover:bg-slate-50 align-middle">
                    <td className="border-r border-black p-2 font-bold text-center">
                      {(filters.currentPage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td className="border-r border-black p-1 text-center font-semibold text-blue-600">
                      {caseNo}
                    </td>
                    <td className="border-r border-black p-1 px-2 text-center">
                      {plaintiffName}
                    </td>
                    <td className="border-r border-black p-1 px-2 text-center">
                      {defendantName}
                    </td>
                    <td className="border-r border-black p-1 text-center font-semibold">
                      {lawSection}
                    </td>
                    <td className="border-r border-black p-1 px-2 text-center">
                      {witnessName}
                    </td>
                    <td className="border-r border-black p-1 px-2 text-center">
                      {officerName}
                    </td>
                    <td className="border-r border-black p-1 text-center whitespace-nowrap">
                      {formattedInterviewDate}
                    </td>
                    <td className="border-r border-black p-1 px-2 text-left">
                      {remarks}
                    </td>
                    <td className="p-1 text-center font-sans">
                      {isOriginUser ? (
                        <div className="flex justify-center gap-1 items-center">
                          <button
                            type="button"
                            onClick={() => navigate(`/plo-form-six/edit/${app.documentNumber}`)}
                            className="text-blue-600 hover:text-blue-800 font-medium text-xs focus:outline-none"
                            title="ပြင်ဆင်ရန်"
                          >
                            Edit
                          </button>
                          <span className="text-gray-300">|</span>
                          <button
                            type="button"
                            onClick={() => handleDelete(app.documentNumber)}
                            className="text-red-600 hover:text-red-800 font-medium text-xs focus:outline-none"
                            title="ဖျက်ရန်"
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        <span className="text-slate-400 font-bold">-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={10} className="text-center py-12 text-xs text-slate-400">
                  ကိုက်ညီသော အချက်အလက် ရှာမတွေ့ပါ။
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <TablePagination
          currentPage={filters.currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={filters.setCurrentPage}
        />
      )}
    </div>
  );
}